/**
 * Create Organization Modal
 *
 * Multi-step modal for creating a new organization:
 * Step 1: Organization details form
 * Step 2: Mobile OTP verification
 * Step 3: Email OTP verification
 * Then creates the organization.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, ArrowRight, ArrowLeft, Phone, Mail, CheckCircle2, Loader2, Eye, EyeOff, AlertTriangle } from 'lucide-react';

import {
  Modal,
  Button,
  Input,
  Select,
  Toggle,
  Badge,
} from '@/components/common';
import { organizationService, ApiError } from '@/api/services';
import type { CreateOrganizationRequest } from '@/api/types';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store';

// ============================================
// Plan types from API
// ============================================

interface PlanTermFromApi {
  planTermCode: string;
  termType: string;
  termValue: number;
  price: number;
  actualPrice: number;
  isActive: boolean;
}

interface SubscriptionPlanFromApi {
  code: string;
  name: string;
  orderNumber: number;
  /** Source of truth for plan active-ness ('ACTIVE' / 'INACTIVE'). */
  status?: string;
  /** Legacy flag; not written by the plan form. Only used as a fallback. */
  isActive?: boolean;
}

interface PlanListItem {
  planTerms: PlanTermFromApi[];
  subscriptionPlan: SubscriptionPlanFromApi;
}

interface GroupedPlan {
  code: string;
  name: string;
  orderNumber: number;
  terms: PlanTermFromApi[];
}

function groupPlans(items: PlanListItem[]): GroupedPlan[] {
  const map = new Map<string, GroupedPlan>();
  for (const item of items) {
    const plan = item.subscriptionPlan;
    // Plans are managed via `status`; `isActive` is never written by the plan
    // form. Fall back to it only when the endpoint omits `status`, and treat a
    // missing flag as active so plans are not silently dropped.
    const isPlanActive = plan.status
      ? plan.status.toUpperCase() === 'ACTIVE'
      : plan.isActive !== false;
    if (!isPlanActive) continue;
    const activeTerms = (item.planTerms || []).filter((t) => t.isActive);
    const existing = map.get(plan.code);
    if (existing) {
      existing.terms.push(...activeTerms);
    } else {
      map.set(plan.code, {
        code: plan.code,
        name: plan.name,
        orderNumber: plan.orderNumber,
        terms: activeTerms,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.orderNumber - b.orderNumber);
}

function formatTermLabel(t: PlanTermFromApi): string {
  const unit = t.termType === 'YEAR' ? 'Year' : 'Month';
  const plural = t.termValue > 1 ? 's' : '';
  return `${t.termValue} ${unit}${plural} - ₹${t.price}`;
}

// ============================================
// Types
// ============================================

type Step = 'form' | 'verify-mobile' | 'verify-email';

// ============================================
// Form Schema
// ============================================

const createOrgSchema = z.object({
  orgName: z
    .string()
    .min(1, 'Organization name is required')
    .max(100, 'Organization name must be less than 100 characters'),
  name: z.string().min(1, 'Contact name is required'),
  email: z.string().email('Invalid email address'),
  mobile: z
    .string()
    .min(10, 'Mobile number must be at least 10 digits')
    .max(15, 'Mobile number must be less than 15 digits'),
  password: z.string().min(1, 'Password is required'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
  planCode: z.string().min(1, 'Plan is required'),
  planTermCode: z.string().min(1, 'Plan term is required'),
  paymentAmount: z
    .string()
    .min(1, 'Payment amount is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, 'Must be a valid amount'),
  paymentMode: z.string().min(1, 'Payment mode is required'),
  paymentReference: z.string().optional(),
  isPayrollSpecific: z.boolean(),
  isDefaultContractRequired: z.boolean(),
  isMultipleUnitsAllowed: z.boolean(),
  website: z.string().optional(),
  state: z.string().optional(),
  gstNumber: z.string().optional(),
  employeeRange: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type CreateOrgFormData = z.infer<typeof createOrgSchema>;

// ============================================
// Options
// ============================================

const PAYMENT_MODE_OPTIONS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CARD', label: 'Card' },
  { value: 'CHEQUE', label: 'Cheque' },
];

const EMPLOYEE_RANGE_OPTIONS = [
  { value: '1-10', label: '1 - 10' },
  { value: '11-50', label: '11 - 50' },
  { value: '51-100', label: '51 - 100' },
  { value: '101-500', label: '101 - 500' },
  { value: '501-1000', label: '501 - 1000' },
  { value: '1000+', label: '1000+' },
];

// ============================================
// OTP Input Component
// ============================================

function OtpInput({
  length = 6,
  value,
  onChange,
  disabled,
}: {
  length?: number;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, digit: string) => {
    if (!/^\d*$/.test(digit)) return;
    const newValue = value.split('');
    newValue[index] = digit;
    const joined = newValue.join('').slice(0, length);
    onChange(joined);
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    const focusIndex = Math.min(pasted.length, length - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="h-12 w-12 rounded-md border border-border text-center text-lg font-semibold
            bg-card dark:bg-card-dark dark:border-border-dark
            text-foreground dark:text-foreground-dark
            focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
            disabled:opacity-50 disabled:cursor-not-allowed"
        />
      ))}
    </div>
  );
}

// ============================================
// Step Indicator
// ============================================

const STEPS: { key: Step; label: string }[] = [
  { key: 'form', label: 'Details' },
  { key: 'verify-mobile', label: 'Verify Mobile' },
  { key: 'verify-email', label: 'Verify Email' },
];

function StepIndicator({ currentStep }: { currentStep: Step }) {
  const currentIndex = STEPS.findIndex((s) => s.key === currentStep);

  return (
    <div className="flex items-center justify-center gap-2 mb-5">
      {STEPS.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        return (
          <div key={step.key} className="flex items-center gap-2">
            {index > 0 && (
              <div
                className={`h-px w-8 ${
                  isCompleted ? 'bg-primary' : 'bg-border dark:bg-border-dark'
                }`}
              />
            )}
            <div className="flex items-center gap-1.5">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                  isCompleted
                    ? 'bg-primary text-white'
                    : isCurrent
                      ? 'bg-primary/10 text-primary border border-primary'
                      : 'bg-secondary-100 text-foreground-muted dark:bg-secondary-800 dark:text-foreground-dark-muted'
                }`}
              >
                {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5" /> : index + 1}
              </div>
              <span
                className={`text-xs font-medium ${
                  isCurrent
                    ? 'text-foreground dark:text-foreground-dark'
                    : 'text-foreground-muted dark:text-foreground-dark-muted'
                }`}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Component Props
// ============================================

interface CreateOrganizationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

// ============================================
// Component
// ============================================

export function CreateOrganizationModal({
  open,
  onOpenChange,
  onSuccess,
}: CreateOrganizationModalProps) {
  const toast = useToast();
  const currentUser = useAuthStore((state) => state.user);

  // Step state
  const [step, setStep] = useState<Step>('form');
  const [formData, setFormData] = useState<CreateOrgFormData | null>(null);

  // Subscription plans from API
  const [plans, setPlans] = useState<GroupedPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  // OTP state
  const [mobileOtp, setMobileOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [smsOtpSent, setSmsOtpSent] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);

  // Loading states
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Error state
  const [formError, setFormError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval>>(undefined);

  // Form
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateOrgFormData>({
    resolver: zodResolver(createOrgSchema),
    defaultValues: {
      orgName: '',
      name: '',
      email: '',
      mobile: '',
      password: '',
      confirmPassword: '',
      planCode: '',
      planTermCode: '',
      paymentAmount: '',
      paymentMode: '',
      paymentReference: '',
      isPayrollSpecific: true,
      isDefaultContractRequired: false,
      isMultipleUnitsAllowed: false,
      website: '',
      state: '',
      gstNumber: '',
      employeeRange: '',
    },
  });

  const selectedPlanCode = watch('planCode');
  const selectedPlanTermCode = watch('planTermCode');
  const selectedPaymentMode = watch('paymentMode');
  const isPayrollSpecific = watch('isPayrollSpecific');
  const isDefaultContractRequired = watch('isDefaultContractRequired');
  const isMultipleUnitsAllowed = watch('isMultipleUnitsAllowed');
  const selectedEmployeeRange = watch('employeeRange');

  // Derive plan select options from API data
  const planOptions = plans.map((p) => ({ value: p.code, label: p.name }));

  const selectedPlan = plans.find((p) => p.code === selectedPlanCode);
  const planTermOptions = (selectedPlan?.terms || []).map((t) => ({
    value: t.planTermCode,
    label: formatTermLabel(t),
  }));

  // Fetch allowed subscription plans for current user when modal opens
  useEffect(() => {
    if (!open || !currentUser?.id) return;
    let cancelled = false;
    setIsLoadingPlans(true);
    organizationService
      .getAllowedPlanDetailsByUserId(currentUser.id)
      .then((data: PlanListItem[]) => {
        if (!cancelled) setPlans(groupPlans(data));
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load subscription plans');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPlans(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentUser?.id]);

  // Cleanup cooldown timer
  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    setResendCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Reset all state
  const resetAll = useCallback(() => {
    reset();
    setStep('form');
    setFormData(null);
    setMobileOtp('');
    setEmailOtp('');
    setFormError(null);
    setOtpError(null);
    setSmsOtpSent(false);
    setEmailOtpSent(false);
    setResendCooldown(0);
    setShowPassword(false);
    setShowConfirmPassword(false);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
  }, [reset]);

  const handleClose = () => {
    if (!isSubmitting && !isVerifyingOtp) {
      resetAll();
      onOpenChange(false);
    }
  };

  // ---- Step 1: Form Submit → Send SMS OTP ----
  const onFormSubmit = async (data: CreateOrgFormData) => {
    setFormError(null);
    setFormData(data);

    // If SMS OTP was already sent (user went back and returned), skip re-sending
    if (smsOtpSent) {
      setStep('verify-mobile');
      return;
    }

    setIsSendingOtp(true);
    try {
      await organizationService.sendSmsOtp({ mobile: data.mobile });
      setSmsOtpSent(true);
      setStep('verify-mobile');
      startCooldown(60);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.showMessage);
      } else {
        setFormError('Failed to send SMS OTP. Please try again.');
      }
    } finally {
      setIsSendingOtp(false);
    }
  };

  // ---- Step 2: Verify Mobile OTP → Send Email OTP ----
  const handleVerifyMobile = async () => {
    if (mobileOtp.length !== 6 || !formData) return;
    setOtpError(null);
    setIsVerifyingOtp(true);

    try {
      await organizationService.verifySmsOtp({
        mobile: formData.mobile,
        otp: mobileOtp,
      });
    } catch (error) {
      if (error instanceof ApiError) {
        setOtpError(error.showMessage);
      } else {
        setOtpError('Mobile OTP verification failed. Please try again.');
      }
      setIsVerifyingOtp(false);
      return;
    }
    setIsVerifyingOtp(false);

    // Mobile verified — send email OTP if not already sent
    if (!emailOtpSent) {
      setIsSendingOtp(true);
      try {
        await organizationService.sendEmailOtp({
          email: formData.email,
        });
        setEmailOtpSent(true);
        setResendCooldown(0);
        startCooldown(60);
      } catch (error) {
        if (error instanceof ApiError) {
          setOtpError(error.showMessage);
        } else {
          setOtpError('Failed to send email OTP. Please try again.');
        }
        setIsSendingOtp(false);
        return;
      }
      setIsSendingOtp(false);
    }
    setStep('verify-email');
  };

  // ---- Step 3: Verify Email OTP → Create Organization ----
  const handleVerifyEmail = async () => {
    if (emailOtp.length !== 6 || !formData) return;
    setOtpError(null);
    setIsVerifyingOtp(true);

    try {
      await organizationService.verifyEmailOtp({
        email: formData.email,
        otp: emailOtp,
      });
    } catch (error) {
      if (error instanceof ApiError) {
        setOtpError(error.showMessage);
      } else {
        setOtpError('Email verification failed. Please try again.');
      }
      setIsVerifyingOtp(false);
      return;
    }
    setIsVerifyingOtp(false);

    // Email verified — now create organization
    setIsSubmitting(true);
    try {
      const request: CreateOrganizationRequest = {
        orgName: formData.orgName,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        name: formData.name,
        planCode: formData.planCode,
        planTermCode: formData.planTermCode,
        paymentAmount: Number(formData.paymentAmount),
        paymentMode: formData.paymentMode,
        paymentReference: formData.paymentReference || undefined,
        isPayrollSpecific: formData.isPayrollSpecific,
        isDefaultContractRequired: formData.isDefaultContractRequired,
        isMultipleUnitsAllowed: formData.isMultipleUnitsAllowed,
        website: formData.website || undefined,
        state: formData.state || undefined,
        gstNumber: formData.gstNumber || undefined,
        employeeRange: formData.employeeRange || undefined,
      };

      await organizationService.createOrganization(request);
      toast.success('Organization created successfully');
      resetAll();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      if (error instanceof ApiError) {
        setOtpError(error.showMessage);
      } else {
        setOtpError('Failed to create organization. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Resend OTP ----
  const handleResendSmsOtp = async () => {
    if (resendCooldown > 0 || !formData) return;
    setOtpError(null);
    setIsSendingOtp(true);
    try {
      await organizationService.sendSmsOtp({ mobile: formData.mobile });
      setMobileOtp('');
      toast.success('OTP sent to your mobile');
      startCooldown(60);
    } catch (error) {
      if (error instanceof ApiError) {
        setOtpError(error.showMessage);
      } else {
        setOtpError('Failed to resend OTP.');
      }
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendEmailOtp = async () => {
    if (resendCooldown > 0 || !formData) return;
    setOtpError(null);
    setIsSendingOtp(true);
    try {
      await organizationService.sendEmailOtp({
        email: formData.email,
      });
      setEmailOtp('');
      toast.success('OTP sent to your email');
      startCooldown(60);
    } catch (error) {
      if (error instanceof ApiError) {
        setOtpError(error.showMessage);
      } else {
        setOtpError('Failed to resend OTP.');
      }
    } finally {
      setIsSendingOtp(false);
    }
  };

  // ---- Step titles ----
  const stepTitle: Record<Step, string> = {
    form: 'Create Organization',
    'verify-mobile': 'Verify Mobile Number',
    'verify-email': 'Verify Email Address',
  };

  const stepDescription: Record<Step, string> = {
    form: 'Fill in organization details and subscription plan',
    'verify-mobile': `Enter the 6-digit OTP sent to ${formData?.mobile || ''}`,
    'verify-email': `Enter the 6-digit OTP sent to ${formData?.email || ''}`,
  };

  const hasNoPlans = !isLoadingPlans && plans.length === 0;

  // ---- Footer ----
  const renderFooter = () => {
    if (step === 'form' && hasNoPlans) {
      return (
        <Button type="button" variant="outline" onClick={handleClose}>
          Close
        </Button>
      );
    }

    if (step === 'form') {
      return (
        <>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            loading={isSendingOtp}
            rightIcon={<ArrowRight className="h-4 w-4" />}
            onClick={handleSubmit(onFormSubmit)}
          >
            Next: Verify Mobile
          </Button>
        </>
      );
    }

    if (step === 'verify-mobile') {
      return (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setStep('form');
              setOtpError(null);
              setMobileOtp('');
            }}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back
          </Button>
          <Button
            loading={isVerifyingOtp}
            disabled={mobileOtp.length !== 6}
            rightIcon={<ArrowRight className="h-4 w-4" />}
            onClick={handleVerifyMobile}
          >
            Verify & Next
          </Button>
        </>
      );
    }

    if (step === 'verify-email') {
      return (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setStep('verify-mobile');
              setOtpError(null);
              setEmailOtp('');
            }}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            disabled={isSubmitting}
          >
            Back
          </Button>
          <Button
            loading={isVerifyingOtp || isSubmitting}
            disabled={emailOtp.length !== 6}
            leftIcon={<Save className="h-4 w-4" />}
            onClick={handleVerifyEmail}
          >
            Verify & Create
          </Button>
        </>
      );
    }

    return null;
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={stepTitle[step]}
      description={stepDescription[step]}
      size="full"
      closeOnOverlayClick={!isSubmitting && !isVerifyingOtp}
      footer={renderFooter()}
    >
      <StepIndicator currentStep={step} />

      {/* ===== Step 1: Organization Form ===== */}
      {step === 'form' && hasNoPlans && (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning/10 mb-4">
            <AlertTriangle className="h-8 w-8 text-warning" />
          </div>
          <h3 className="text-lg font-semibold text-foreground dark:text-foreground-dark">
            No Subscription Plans Available
          </h3>
          <p className="mt-2 text-sm text-foreground-muted dark:text-foreground-dark-muted max-w-sm">
            You don't have any subscription plans assigned to your account. Please contact your administrator to get plans assigned before creating an organization.
          </p>
        </div>
      )}

      {step === 'form' && !hasNoPlans && (
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {formError && (
            <div className="rounded-lg bg-danger/10 border border-danger/20 px-4 py-3">
              <p className="text-sm text-danger dark:text-danger-dark">{formError}</p>
            </div>
          )}

          {/* Section: Organization Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground dark:text-foreground-dark border-b border-border dark:border-border-dark pb-2">
              Organization Information
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Organization Name"
                placeholder="Enter organization name"
                required
                error={errors.orgName?.message}
                {...register('orgName')}
              />
              <Input
                label="Contact Name"
                placeholder="Enter contact person name"
                required
                error={errors.name?.message}
                {...register('name')}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Email"
                type="email"
                placeholder="Enter email address"
                required
                error={errors.email?.message}
                {...register('email')}
              />
              <Input
                label="Mobile"
                placeholder="Enter mobile number"
                required
                error={errors.mobile?.message}
                {...register('mobile')}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                required
                error={errors.password?.message}
                rightElement={
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-foreground-muted hover:text-foreground dark:hover:text-foreground-dark"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                }
                {...register('password')}
              />
              <Input
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Re-enter password"
                required
                error={errors.confirmPassword?.message}
                rightElement={
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="text-foreground-muted hover:text-foreground dark:hover:text-foreground-dark"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                }
                {...register('confirmPassword')}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Website"
                placeholder="Enter website URL (optional)"
                error={errors.website?.message}
                {...register('website')}
              />
              <Input
                label="State"
                placeholder="Enter state (optional)"
                error={errors.state?.message}
                {...register('state')}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="GST Number"
                placeholder="Enter GST number (optional)"
                error={errors.gstNumber?.message}
                {...register('gstNumber')}
              />
              <Select
                label="Employee Range"
                placeholder="Select range (optional)"
                options={EMPLOYEE_RANGE_OPTIONS}
                value={selectedEmployeeRange || ''}
                onChange={(value) => setValue('employeeRange', value)}
                errorMessage={errors.employeeRange?.message}
              />
            </div>
          </div>

          {/* Section: Subscription & Payment */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground dark:text-foreground-dark border-b border-border dark:border-border-dark pb-2">
              Subscription & Payment
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <Select
                label="Subscription Plan"
                placeholder="Select plan"
                required
                options={planOptions}
                disabled={isLoadingPlans}
                value={selectedPlanCode}
                onChange={(value) => {
                  setValue('planCode', value);
                  setValue('planTermCode', '');
                }}
                errorMessage={errors.planCode?.message}
              />
              <Select
                label="Plan Term"
                placeholder="Select plan term"
                required
                options={planTermOptions}
                value={selectedPlanTermCode}
                onChange={(value) => setValue('planTermCode', value)}
                disabled={!selectedPlanCode}
                errorMessage={errors.planTermCode?.message}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Payment Amount"
                type="number"
                placeholder="Enter amount"
                required
                error={errors.paymentAmount?.message}
                {...register('paymentAmount')}
              />
              <Select
                label="Payment Mode"
                placeholder="Select payment mode"
                required
                options={PAYMENT_MODE_OPTIONS}
                value={selectedPaymentMode}
                onChange={(value) => setValue('paymentMode', value)}
                errorMessage={errors.paymentMode?.message}
              />
              <Input
                label="Payment Reference"
                placeholder="Enter reference (optional)"
                error={errors.paymentReference?.message}
                {...register('paymentReference')}
              />
            </div>
          </div>

          {/* Section: Configuration */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground dark:text-foreground-dark border-b border-border dark:border-border-dark pb-2">
              Configuration
            </h3>
            <div className="space-y-3 rounded-lg border border-border dark:border-border-dark p-5 bg-secondary-50 dark:bg-secondary-900/30">
              <Toggle
                label="Payroll Specific"
                description="Enable payroll-specific features"
                checked={isPayrollSpecific}
                onChange={(checked) => setValue('isPayrollSpecific', checked)}
              />
              <Toggle
                label="Default Contract Required"
                description="Require a default contract on setup"
                checked={isDefaultContractRequired}
                onChange={(checked) => setValue('isDefaultContractRequired', checked)}
              />
              <Toggle
                label="Multiple Units Allowed"
                description="Allow multiple business units"
                checked={isMultipleUnitsAllowed}
                onChange={(checked) => setValue('isMultipleUnitsAllowed', checked)}
              />
            </div>
          </div>
        </form>
      )}

      {/* ===== Step 2: Verify Mobile OTP ===== */}
      {step === 'verify-mobile' && (
        <div className="space-y-6">
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Phone className="h-7 w-7 text-primary" />
            </div>
            <div className="text-center">
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                We sent a 6-digit verification code to
              </p>
              <p className="mt-1 font-semibold text-foreground dark:text-foreground-dark">
                {formData?.mobile}
              </p>
            </div>
          </div>

          {otpError && (
            <div className="rounded-md bg-danger/10 border border-danger/20 p-3">
              <p className="text-sm text-danger dark:text-danger-dark text-center">{otpError}</p>
            </div>
          )}

          <OtpInput
            value={mobileOtp}
            onChange={setMobileOtp}
            disabled={isVerifyingOtp}
          />

          <div className="text-center">
            {resendCooldown > 0 ? (
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                Resend OTP in <span className="font-medium text-primary">{resendCooldown}s</span>
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendSmsOtp}
                disabled={isSendingOtp}
                className="text-sm font-medium text-primary hover:text-primary/80 disabled:opacity-50"
              >
                {isSendingOtp ? (
                  <span className="flex items-center justify-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Sending...
                  </span>
                ) : (
                  'Resend OTP'
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ===== Step 3: Verify Email OTP ===== */}
      {step === 'verify-email' && (
        <div className="space-y-6">
          {/* Verified badge for mobile */}
          <div className="flex items-center justify-center">
            <Badge variant="success" dot>
              Mobile verified
            </Badge>
          </div>

          <div className="flex flex-col items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-7 w-7 text-primary" />
            </div>
            <div className="text-center">
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                We sent a 6-digit verification code to
              </p>
              <p className="mt-1 font-semibold text-foreground dark:text-foreground-dark">
                {formData?.email}
              </p>
            </div>
          </div>

          {otpError && (
            <div className="rounded-md bg-danger/10 border border-danger/20 p-3">
              <p className="text-sm text-danger dark:text-danger-dark text-center">{otpError}</p>
            </div>
          )}

          <OtpInput
            value={emailOtp}
            onChange={setEmailOtp}
            disabled={isVerifyingOtp || isSubmitting}
          />

          <div className="text-center">
            {resendCooldown > 0 ? (
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                Resend OTP in <span className="font-medium text-primary">{resendCooldown}s</span>
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendEmailOtp}
                disabled={isSendingOtp}
                className="text-sm font-medium text-primary hover:text-primary/80 disabled:opacity-50"
              >
                {isSendingOtp ? (
                  <span className="flex items-center justify-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Sending...
                  </span>
                ) : (
                  'Resend OTP'
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
